export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly emailVerified: boolean;
}

export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
}

export interface SignupCredentials {
  readonly name: string;
  readonly email: string;
  readonly password: string;
  // Optional management invite token. Signup is open to everyone; a valid token
  // (e.g. from a staff /signup?token=... link) pre-verifies the new account.
  readonly invite_token?: string;
}

export interface PlayerProfile {
  readonly id: string;
  readonly name: string;
  readonly total_points: number;
  readonly created_at: string;
}

export interface UpdateNameRequest {
  readonly name: string;
}

export interface DeleteAccountRequest {
  readonly password: string;
}

export type AuthErrorType =
  | 'invalid_credentials'
  | 'email_taken'
  | 'name_taken'
  | 'invalid_token'
  // The server intentionally conflates invalid and expired tokens (returns the same
  // 400 status for both) to prevent enumeration. This type exists so the UI can
  // handle both cases in a single check without special-casing.
  | 'expired_token'
  | 'account_locked'
  | 'already_verified'
  | 'validation'
  | 'rate_limited'
  | 'network'
  | 'unknown';

export interface AuthError {
  readonly type: AuthErrorType;
  readonly message: string;
  readonly retryAfterSeconds?: number;
}

const AUTH_ERROR_TYPES: ReadonlySet<string> = new Set<AuthErrorType>([
  'invalid_credentials',
  'email_taken',
  'name_taken',
  'invalid_token',
  'expired_token',
  'account_locked',
  'already_verified',
  'validation',
  'rate_limited',
  'network',
  'unknown',
]);

export function isAuthError(value: unknown): value is AuthError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'message' in value &&
    typeof (value as AuthError).type === 'string' &&
    AUTH_ERROR_TYPES.has((value as AuthError).type) &&
    typeof (value as AuthError).message === 'string'
  );
}

export function isAuthUser(value: unknown): value is AuthUser {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'name' in value &&
    'emailVerified' in value &&
    typeof (value as AuthUser).id === 'string' &&
    typeof (value as AuthUser).name === 'string' &&
    typeof (value as AuthUser).emailVerified === 'boolean'
  );
}

export function isPlayerProfile(value: unknown): value is PlayerProfile {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'name' in value &&
    'total_points' in value &&
    'created_at' in value &&
    typeof (value as PlayerProfile).id === 'string' &&
    typeof (value as PlayerProfile).name === 'string' &&
    typeof (value as PlayerProfile).total_points === 'number' &&
    typeof (value as PlayerProfile).created_at === 'string'
  );
}

export function createAuthError(
  type: AuthErrorType,
  message: string,
  retryAfterSeconds?: number
): AuthError {
  return retryAfterSeconds !== undefined ? { type, message, retryAfterSeconds } : { type, message };
}

/**
 * Machine-readable error codes returned by the server in `{ "code": "..." }` error bodies.
 * Must stay in sync with `AuthErrorCode` in
 * `server/crates/lexicontest-server/src/handlers/auth.rs`.
 */
export type AuthServerErrorCode =
  | 'email_already_exists'
  | 'name_already_exists'
  | 'invalid_credentials'
  | 'invalid_token'
  | 'account_locked'
  | 'already_verified'
  | 'validation_error'
  | 'rate_limited'
  | 'not_found'
  | 'unauthorized'
  | 'internal_error';

const AUTH_SERVER_ERROR_CODES: ReadonlySet<string> = new Set<AuthServerErrorCode>([
  'email_already_exists',
  'name_already_exists',
  'invalid_credentials',
  'invalid_token',
  'account_locked',
  'already_verified',
  'validation_error',
  'rate_limited',
  'not_found',
  'unauthorized',
  'internal_error',
]);

const SERVER_CODE_TO_AUTH_ERROR_TYPE: Record<AuthServerErrorCode, AuthErrorType> = {
  email_already_exists: 'email_taken',
  name_already_exists: 'name_taken',
  invalid_credentials: 'invalid_credentials',
  invalid_token: 'invalid_token',
  account_locked: 'account_locked',
  already_verified: 'already_verified',
  validation_error: 'validation',
  rate_limited: 'rate_limited',
  not_found: 'unknown',
  unauthorized: 'unknown',
  internal_error: 'unknown',
};

export function toAuthErrorType(code: AuthServerErrorCode): AuthErrorType {
  return SERVER_CODE_TO_AUTH_ERROR_TYPE[code] ?? 'unknown';
}

export function isAuthErrorBody(value: unknown): value is { code: AuthServerErrorCode } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof (value as { code: unknown }).code === 'string' &&
    AUTH_SERVER_ERROR_CODES.has((value as { code: string }).code)
  );
}
